import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { IContractVariable } from '@clickersphere/core/entities/IContractVariable';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { ContractAnalysisReasonEnum } from '@clickersphere/core/enums/ContractAnalysisReasonEnum';
import { ContractAnalysisStatusEnum } from '@clickersphere/core/enums/ContractAnalysisStatusEnum';
import { ContractVariableSourceEnum } from '@clickersphere/core/enums/ContractVariableSourceEnum';
import {
  IAnalyzeContractTemplateResult,
  IContractTemplateParagraph,
} from '@clickersphere/core/services/ContractTemplateService';
import { useAnalyzeContractTemplate } from '../../../../app/hooks/contract-template/useAnalyzeContractTemplate';
import { useContractTemplateParagraphs } from '../../../../app/hooks/contract-template/useContractTemplateParagraphs';
import { useExtractContractTemplateParagraphs } from '../../../../app/hooks/contract-template/useExtractContractTemplateParagraphs';
import { useCreateContractTemplate } from '../../../../app/hooks/contract-template/useCreateContractTemplate';
import { useUpdateContractTemplate } from '../../../../app/hooks/contract-template/useUpdateContractTemplate';
import {
  TEMPLATE_UPLOAD_FILE_TOO_LARGE_ERROR,
  TEMPLATE_UPLOAD_INVALID_FILE_ERROR,
  useUploadContractTemplateFile,
} from '../../../../app/hooks/contract-template/useUploadContractTemplateFile';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import { deriveVariableKey } from '../../../../app/utils/deriveVariableKey';
import {
  IEditableContractVariable,
  IVariableRowErrors,
} from './VariableMappingEditor';

type TemplateWizardStep = 'upload' | 'variables';

export type EditorMobileTab = 'document' | 'variables';

export interface IAnchorSelection {
  paragraphIndex: number;
  matchText: string;
}

export interface ICreateAnchorPayload {
  targetRowId?: string;
  newVariable?: {
    label: string;
    source: ContractVariableSourceEnum;
    required: boolean;
  };
  anchor: IAnchorSelection;
  applyToOtherOccurrences?: boolean;
}

const TEMPLATE_NAME_MAX_LENGTH = 100;

function toEditableVariables(
  variables: IContractVariable[],
): IEditableContractVariable[] {
  return variables.map((variable) => ({
    ...variable,
    anchors: variable.anchors ?? [],
    rowId: crypto.randomUUID(),
  }));
}

export function useTemplateModalController() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();

  const { contractTemplate, handleCloseModal } = useStore(
    useShallow((state) => ({
      contractTemplate: state.modal.contractTemplate,
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );

  const isEditing = !!contractTemplate;

  const [step, setStep] = useState<TemplateWizardStep>(
    isEditing ? 'variables' : 'upload',
  );
  const [name, setName] = useState(contractTemplate?.name ?? '');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [active, setActive] = useState(contractTemplate?.active ?? true);
  const [docxFileKey, setDocxFileKey] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [analysisStatus, setAnalysisStatus] =
    useState<ContractAnalysisStatusEnum | null>(null);
  const [analysisReason, setAnalysisReason] =
    useState<ContractAnalysisReasonEnum | null>(null);
  const [analysisVariableCount, setAnalysisVariableCount] = useState(0);
  const [analyzedParagraphs, setAnalyzedParagraphs] = useState<
    IContractTemplateParagraph[]
  >([]);
  const [variables, setVariables] = useState<IEditableContractVariable[]>(
    toEditableVariables(contractTemplate?.variablesSchema ?? []),
  );
  const [variableErrors, setVariableErrors] = useState<
    Record<string, IVariableRowErrors>
  >({});
  const [confirmMissingAnchors, setConfirmMissingAnchors] = useState(false);
  const [mobileTab, setMobileTab] = useState<EditorMobileTab>('document');

  const {
    uploadContractTemplateFileAsync,
    abortUpload,
    progress: uploadProgress,
    isUploading,
  } = useUploadContractTemplateFile();
  const {
    analyzeContractTemplateAsync,
    analyzeContractTemplateIsPending,
    resetAnalyzeContractTemplate,
  } = useAnalyzeContractTemplate();
  const { extractParagraphsAsync, extractParagraphsIsPending } =
    useExtractContractTemplateParagraphs();
  const { createContractTemplateAsync, createContractTemplateIsPending } =
    useCreateContractTemplate();
  const { updateContractTemplateAsync, updateContractTemplateIsPending } =
    useUpdateContractTemplate();

  const { paragraphs: editParagraphs, isParagraphsLoading } =
    useContractTemplateParagraphs(contractTemplate?.id, {
      enabled: isEditing,
    });

  const paragraphs = isEditing ? editParagraphs : analyzedParagraphs;
  const isParagraphsLoadingResolved = isEditing && isParagraphsLoading;

  const isSaving =
    createContractTemplateIsPending || updateContractTemplateIsPending;
  const isProcessingFile = isUploading || extractParagraphsIsPending;

  function validateName(value: string): string | undefined {
    const trimmed = value.trim();
    if (!trimmed) return t('contract.templates.validation.nameRequired');
    if (trimmed.length > TEMPLATE_NAME_MAX_LENGTH) {
      return t('contract.templates.validation.nameMaxLength');
    }
    return undefined;
  }

  function handleNameChange(value: string) {
    setName(value);
    setNameError(undefined);
  }

  function applyAnalysisResult(result: IAnalyzeContractTemplateResult) {
    setAnalysisStatus(result.analysisStatus ?? ContractAnalysisStatusEnum.AI);
    setAnalysisReason(result.analysisReason ?? null);
    setAnalysisVariableCount(result.variables.length);
  }

  function handleAnalysisError(error: unknown) {
    if (error instanceof ResponseError) {
      handleError(error);
    } else {
      toast(t('contract.templates.errors.analyzeUnreachable'), 'error');
    }

    setAnalysisStatus(ContractAnalysisStatusEnum.MANUAL);
    setAnalysisReason(ContractAnalysisReasonEnum.UNAVAILABLE);
  }

  function handleParagraphsError(error: unknown) {
    setAnalyzedParagraphs([]);

    if (error instanceof ResponseError) {
      if (error.code === 'CONTRACT-0002') {
        setDocxFileKey(null);
        setSelectedFileName(null);
        setFileError(t('contract.templates.errors.invalidFile'));
        return;
      }

      handleError(error);
      return;
    }

    setFileError(t('contract.templates.errors.paragraphsUnreachable'));
  }

  async function handleFileSelected(file: File) {
    setFileError(undefined);
    setAnalysisStatus(null);
    setAnalysisReason(null);
    setAnalysisVariableCount(0);
    setAnalyzedParagraphs([]);
    setVariables([]);
    resetAnalyzeContractTemplate();
    setSelectedFileName(file.name);

    let fileKey: string;
    try {
      const result = await uploadContractTemplateFileAsync(file);
      fileKey = result.fileKey;
      setDocxFileKey(fileKey);
    } catch (error) {
      setSelectedFileName(null);
      setDocxFileKey(null);

      if (error instanceof Error) {
        if (error.message === TEMPLATE_UPLOAD_INVALID_FILE_ERROR) {
          setFileError(t('contract.templates.errors.invalidFile'));
          return;
        }
        if (error.message === TEMPLATE_UPLOAD_FILE_TOO_LARGE_ERROR) {
          setFileError(t('contract.templates.errors.fileTooLarge'));
          return;
        }
      }

      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }

      setFileError(t('contract.templates.errors.uploadFailed'));
      return;
    }

    try {
      const result = await extractParagraphsAsync(fileKey);
      setAnalyzedParagraphs(result.paragraphs);
    } catch (error) {
      handleParagraphsError(error);
    }
  }

  function mergeAnalyzedVariables(suggested: IContractVariable[]) {
    setVariables((old) => {
      if (old.length === 0) return toEditableVariables(suggested);

      const mappedKeys = new Set(old.map((variable) => variable.key.trim()));
      const additions = suggested.filter(
        (variable) => !mappedKeys.has(variable.key.trim()),
      );

      return [...old, ...toEditableVariables(additions)];
    });
  }

  async function handleSuggestVariables() {
    if (!docxFileKey || isProcessingFile || analyzeContractTemplateIsPending) {
      return;
    }

    resetAnalyzeContractTemplate();

    try {
      const result = await analyzeContractTemplateAsync(docxFileKey);
      mergeAnalyzedVariables(result.variables);
      applyAnalysisResult(result);
    } catch (error) {
      handleAnalysisError(error);
    }
  }

  function handleContinue() {
    const error = validateName(name);
    if (error) {
      setNameError(error);
      return;
    }

    if (!docxFileKey) {
      setFileError(t('contract.templates.validation.fileRequired'));
      return;
    }

    setStep('variables');
  }

  function handleBack() {
    setStep('upload');
  }

  function clearRowError(rowId: string) {
    setVariableErrors((old) => {
      if (!old[rowId]) return old;
      const next = { ...old };
      delete next[rowId];
      return next;
    });
  }

  function handleVariableChange(
    rowId: string,
    patch: Partial<IContractVariable>,
  ) {
    setVariables((old) =>
      old.map((variable) =>
        variable.rowId === rowId ? { ...variable, ...patch } : variable,
      ),
    );
    clearRowError(rowId);
  }

  function handleVariableRemove(rowId: string) {
    setVariables((old) => old.filter((variable) => variable.rowId !== rowId));
  }

  function handleVariableAdd() {
    setVariables((old) => [
      ...old,
      {
        rowId: crypto.randomUUID(),
        key: '',
        label: '',
        source: ContractVariableSourceEnum.CUSTOM,
        required: false,
        anchors: [],
      },
    ]);
  }

  function expandAnchors(
    anchor: IAnchorSelection,
    applyToOtherOccurrences?: boolean,
  ): IAnchorSelection[] {
    if (!applyToOtherOccurrences) return [anchor];

    const paragraphIndexes = new Set<number>([anchor.paragraphIndex]);
    paragraphs.forEach((paragraph) => {
      if (paragraph.text.includes(anchor.matchText)) {
        paragraphIndexes.add(paragraph.index);
      }
    });

    return [...paragraphIndexes]
      .sort((a, b) => a - b)
      .map((paragraphIndex) => ({
        paragraphIndex,
        matchText: anchor.matchText,
      }));
  }

  function mergeAnchors(
    existing: IAnchorSelection[],
    incoming: IAnchorSelection[],
  ): IAnchorSelection[] {
    const seen = new Set(
      existing.map((anchor) => `${anchor.paragraphIndex} ${anchor.matchText}`),
    );
    const merged = [...existing];

    incoming.forEach((anchor) => {
      const identity = `${anchor.paragraphIndex} ${anchor.matchText}`;
      if (seen.has(identity)) return;
      seen.add(identity);
      merged.push(anchor);
    });

    return merged;
  }

  function handleCreateAnchor(payload: ICreateAnchorPayload) {
    const incoming = expandAnchors(
      payload.anchor,
      payload.applyToOtherOccurrences,
    );

    if (payload.targetRowId) {
      setVariables((old) =>
        old.map((variable) =>
          variable.rowId === payload.targetRowId
            ? { ...variable, anchors: mergeAnchors(variable.anchors, incoming) }
            : variable,
        ),
      );
      return;
    }

    const { newVariable } = payload;
    if (!newVariable) return;

    setVariables((old) => [
      ...old,
      {
        rowId: crypto.randomUUID(),
        key: deriveVariableKey(newVariable.label),
        label: newVariable.label.trim(),
        source: newVariable.source,
        required: newVariable.required,
        anchors: incoming,
      },
    ]);
  }

  function handleRemoveAnchor(rowId: string, anchorIndex: number) {
    setVariables((old) =>
      old.map((variable) =>
        variable.rowId === rowId
          ? {
              ...variable,
              anchors: variable.anchors.filter(
                (_, index) => index !== anchorIndex,
              ),
            }
          : variable,
      ),
    );
  }

  function handleRemoveAnchorByMatch(
    varKey: string,
    paragraphIndex: number,
    matchText: string,
  ) {
    setVariables((old) =>
      old.map((variable) =>
        variable.key === varKey
          ? {
              ...variable,
              anchors: variable.anchors.filter(
                (anchor) =>
                  !(
                    anchor.paragraphIndex === paragraphIndex &&
                    anchor.matchText === matchText
                  ),
              ),
            }
          : variable,
      ),
    );
  }

  function handleLocateVariable(varKey: string) {
    setMobileTab('document');

    window.setTimeout(() => {
      const target = document.querySelector<HTMLElement>(
        `[data-editor-document] [data-var-key="${varKey}"]`,
      );
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  }

  function validateVariables(): boolean {
    const errors: Record<string, IVariableRowErrors> = {};
    const keyCount = new Map<string, number>();

    variables.forEach((variable) => {
      const key = variable.key.trim();
      keyCount.set(key, (keyCount.get(key) ?? 0) + 1);
    });

    variables.forEach((variable) => {
      const rowErrors: IVariableRowErrors = {};
      const key = variable.key.trim();

      if (!key) {
        rowErrors.key = t(
          'contract.templates.variables.validation.keyRequired',
        );
      } else if ((keyCount.get(key) ?? 0) > 1) {
        rowErrors.key = t(
          'contract.templates.variables.validation.keyDuplicated',
        );
      }

      if (!variable.label.trim()) {
        rowErrors.label = t(
          'contract.templates.variables.validation.labelRequired',
        );
      }

      if (rowErrors.key || rowErrors.label) {
        errors[variable.rowId] = rowErrors;
      }
    });

    setVariableErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const variablesSchema = useMemo<IContractVariable[]>(
    () =>
      variables.map((variable) => ({
        key: variable.key.trim(),
        label: variable.label.trim(),
        source: variable.source,
        required: variable.required,
        anchors: variable.anchors,
      })),
    [variables],
  );

  const requiredWithoutAnchor = useMemo(
    () =>
      variables.filter(
        (variable) => variable.required && variable.anchors.length === 0,
      ),
    [variables],
  );

  const hasSelectableText = useMemo(
    () =>
      paragraphs.length > 0 &&
      paragraphs.some((paragraph) => paragraph.text.trim() !== ''),
    [paragraphs],
  );

  const canSuggestVariables = !isEditing && !!docxFileKey && hasSelectableText;

  const showEmptyDocumentWarning =
    !isEditing && !!docxFileKey && !isProcessingFile && !hasSelectableText;

  async function performSave() {
    try {
      if (!isEditing) {
        if (!docxFileKey) {
          setFileError(t('contract.templates.validation.fileRequired'));
          setStep('upload');
          return;
        }

        await createContractTemplateAsync({
          name: name.trim(),
          docxFileKey,
          variablesSchema,
        });
        toast(t('contract.templates.toast.created'), 'success');
      } else {
        await updateContractTemplateAsync({
          id: contractTemplate.id,
          data: {
            name: name.trim(),
            variablesSchema,
            active,
          },
        });
        toast(t('contract.templates.toast.updated'), 'success');
      }
      handleCloseModal();
    } catch (err) {
      if (err instanceof ResponseError) {
        handleError(err);
        return;
      }
      toast(
        isEditing
          ? t('contract.templates.toast.error.update')
          : t('contract.templates.toast.error.create'),
        'error',
      );
    }
  }

  async function handleSubmit() {
    if (variables.every((variable) => variable.anchors.length === 0)) {
      toast(t('contract.templates.validation.noAnchors'), 'error');
      setMobileTab('document');
      return;
    }

    const error = validateName(name);
    if (error) {
      setNameError(error);
      return;
    }

    if (!validateVariables()) return;

    if (requiredWithoutAnchor.length > 0) {
      setConfirmMissingAnchors(true);
      return;
    }

    await performSave();
  }

  function handleConfirmMissingAnchors() {
    setConfirmMissingAnchors(false);
    void performSave();
  }

  function handleCancelMissingAnchors() {
    setConfirmMissingAnchors(false);
  }

  function handleClose() {
    abortUpload();
    handleCloseModal();
  }

  return {
    t,
    isEditing,
    step,
    name,
    nameError,
    active,
    selectedFileName,
    fileError,
    analysisStatus,
    analysisReason,
    analysisVariableCount,
    canSuggestVariables,
    showEmptyDocumentWarning,
    hasSelectableText,
    docxFileKey,
    paragraphs,
    isParagraphsLoading: isParagraphsLoadingResolved,
    variables,
    variableErrors,
    confirmMissingAnchors,
    mobileTab,
    uploadProgress,
    isUploading,
    isAnalyzing: analyzeContractTemplateIsPending,
    isExtractingParagraphs: extractParagraphsIsPending,
    isProcessingFile,
    isSaving,
    handleNameChange,
    handleActiveChange: setActive,
    handleFileSelected,
    handleSuggestVariables,
    handleContinue,
    handleBack,
    handleVariableChange,
    handleVariableRemove,
    handleVariableAdd,
    handleCreateAnchor,
    handleRemoveAnchor,
    handleRemoveAnchorByMatch,
    handleLocateVariable,
    handleSubmit,
    handleConfirmMissingAnchors,
    handleCancelMissingAnchors,
    handleClose,
    handleMobileTabChange: setMobileTab,
  };
}
